/**
 * FMF 2027 - Conference Agenda page
 * Day tabs + filter accordion (functional behaviour only;
 * visual styling lives in
 * src/scss/sections/fmf-2027-conference-agenda.scss).
 */
export function initConferenceAgenda() {
  const page = document.querySelector('.fmf-conference-agenda');
  if (!page) return;

  // -- Day tabs: switch active pill + show the matching day panel --
  // Filter / category / search live outside the panels, so they persist.
  const tabs = page.querySelectorAll('.fmf-ca-tab');
  const dayPanels = page.querySelectorAll('.fmf-ca-day');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.classList.toggle('is-active', t === tab);
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
      });
      dayPanels.forEach((panel) => {
        panel.hidden = panel.dataset.day !== tab.dataset.day;
      });
      closeCalMenu();
    });
  });

  // -- Filter accordion (opens the dropdown panel) --
  const filterBtn = page.querySelector('.fmf-ca-filter');
  const filterBody = page.querySelector('.fmf-ca-filter-body');
  const filterBadge = page.querySelector('.fmf-ca-filter-badge');

  const UNCHECKED_IMG = '../assets/images/fmf-2027/fmf-ca-checkbox-default.svg';
  const CHECKED_IMG = '../assets/images/fmf-2027/fmf-ca-checkbox-active.svg';

  const updateFilterBadge = () => {
    if (!filterBadge) return;
    const count = page.querySelectorAll('.fmf-ca-option.is-checked').length;
    filterBadge.textContent = String(count);
    filterBadge.hidden = count === 0;
  };

  // Select head per Figma variants (19569:16853 "Selected Single",
  // 19569:17066 "Multi Selected") - the head literally reads "Selected":
  //   0 checked  -> placeholder only ("Select Theme")
  //   1 checked  -> "Selected"
  //   N checked  -> "Selected" + green "& (N-1) more" chip
  const updateSelectSummary = (select) => {
    const textEl = select.querySelector('.fmf-ca-select-text');
    const moreEl = select.querySelector('.fmf-ca-more-badge'); // absent for single-select
    const placeholder = select.querySelector('.fmf-ca-select-placeholder');
    if (!textEl) return;
    const checked = select.querySelectorAll('.fmf-ca-option.is-checked');
    if (checked.length === 0) {
      textEl.hidden = true;
      if (moreEl) { moreEl.hidden = true; moreEl.textContent = ''; }
      if (placeholder) placeholder.hidden = false;
      textEl.textContent = '';
    } else {
      if (placeholder) placeholder.hidden = true;
      textEl.hidden = false;
      textEl.textContent = 'Selected';
      if (moreEl) {
        if (checked.length > 1) {
          moreEl.hidden = false;
          moreEl.textContent = '& ' + (checked.length - 1) + ' more';
        } else {
          moreEl.hidden = true;
        }
      }
    }
  };

  const clearOptions = (scope) => {
    scope.querySelectorAll('.fmf-ca-option.is-checked').forEach((option) => {
      option.classList.remove('is-checked');
      const img = option.querySelector('.fmf-ca-checkbox img');
      if (img) img.src = UNCHECKED_IMG;
    });
    // The scope may be a single select (per-dropdown Clear all) or a
    // container (panel-wide Clear All) - cover both.
    const selects = scope.matches('.fmf-ca-select')
      ? [scope]
      : Array.from(scope.querySelectorAll('.fmf-ca-select'));
    selects.forEach(updateSelectSummary);
    updateFilterBadge();
  };

  // Sync summaries/badge with options already checked in the markup
  // (e.g. "List Item 3"), so the initial state matches what a click would produce
  page.querySelectorAll('.fmf-ca-select').forEach(updateSelectSummary);
  updateFilterBadge();

  const closeFilter = () => {
    filterBtn.setAttribute('aria-expanded', 'false');
    filterBody.hidden = true;
  };

  if (filterBtn && filterBody) {
    filterBtn.addEventListener('click', () => {
      const expanded = filterBtn.getAttribute('aria-expanded') === 'true';
      filterBtn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      filterBody.hidden = expanded;
    });

    // Close (X) button in the mobile overlay's own header (Figma "Filter DDL")
    const filterCloseBtn = filterBody.querySelector('.fmf-ca-filter-close');
    if (filterCloseBtn) {
      filterCloseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeFilter();
      });
    }
  }

  // -- Filter dropdown selects: toggle option lists, checkboxes, counts --
  page.querySelectorAll('.fmf-ca-select').forEach((select) => {
    const head = select.querySelector('.fmf-ca-select-head');
    if (!head) return;

    // Clicking anywhere on the select card (border, padding, head) opens/closes
    // the dropdown. Clicks inside the option list or actions row are excluded
    // so they can handle their own events without re-triggering the toggle.
    select.addEventListener('click', (e) => {
      const list    = select.querySelector('.fmf-ca-select-list');
      const actions = select.querySelector('.fmf-ca-select-actions');
      if ((list && list.contains(e.target)) || (actions && actions.contains(e.target))) return;
      const open = head.getAttribute('aria-expanded') === 'true';
      head.setAttribute('aria-expanded', open ? 'false' : 'true');
      select.classList.toggle('is-open', !open);
    });

    // Checkbox rows (multi-select): toggle checked state + image
    select.querySelectorAll('.fmf-ca-option:not(.fmf-ca-option--radio)').forEach((option) => {
      option.addEventListener('click', () => {
        const checked = option.classList.toggle('is-checked');
        const img = option.querySelector('.fmf-ca-checkbox img');
        if (img) img.src = checked ? CHECKED_IMG : UNCHECKED_IMG;
        updateSelectSummary(select);
        updateFilterBadge();
      });
    });

    // Radio rows (single-select, Location dropdown): exclusive selection,
    // closes the list and shows the selected item's label in the head
    select.querySelectorAll('.fmf-ca-option--radio').forEach((option) => {
      option.addEventListener('click', () => {
        // Deselect all peers
        select.querySelectorAll('.fmf-ca-option--radio').forEach((o) => o.classList.remove('is-checked'));
        option.classList.add('is-checked');

        // Show selected item label in head
        const textEl = select.querySelector('.fmf-ca-select-text');
        const placeholder = select.querySelector('.fmf-ca-select-placeholder');
        const label = option.querySelector('.fmf-ca-option-label');
        if (textEl) { textEl.textContent = 'Selected'; textEl.hidden = false; }
        if (placeholder) placeholder.hidden = true;

        // Close the dropdown
        head.setAttribute('aria-expanded', 'false');
        select.classList.remove('is-open');
        updateFilterBadge();
      });
    });

    // Per-dropdown "Clear all"
    const clearBtn = select.querySelector('.fmf-ca-mini-link');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        clearOptions(select);
      });
    }
  });  

  // Panel footer: "Clear All" wipes every selection; "Apply filter" closes the
  // panel and leaves the selection count badge on the Filter trigger
  // (actual card filtering is wired later)
  const clearAllBtn = page.querySelector('.fmf-ca-filter-actions .fmf-ca-filter-link:not(.fmf-ca-filter-link-strong)');
  const applyBtn = page.querySelector('.fmf-ca-filter-actions .fmf-ca-filter-link-strong');
  if (clearAllBtn) {
    clearAllBtn.addEventListener('click', () => clearOptions(filterBody || page));
  }
  if (applyBtn && filterBtn && filterBody) {
    applyBtn.addEventListener('click', closeFilter);
  }

  // -- Category: active item switching + mobile dropdown (Figma "DDL") --
  const category = page.querySelector('.fmf-ca-category');
  const categoryTrigger = page.querySelector('.fmf-ca-category-trigger');
  const categoryTriggerValue = page.querySelector('.fmf-ca-category-trigger-value');
  const categoryItems = page.querySelectorAll('.fmf-ca-category-item');

  const closeCategory = () => {
    if (!category || !categoryTrigger) return;
    category.classList.remove('is-open');
    categoryTrigger.setAttribute('aria-expanded', 'false');
  };

  if (category && categoryTrigger) {
    categoryTrigger.addEventListener('click', () => {
      const open = category.classList.toggle('is-open');
      categoryTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  categoryItems.forEach((item) => {
    item.addEventListener('click', () => {
      categoryItems.forEach((c) => c.classList.toggle('is-active', c === item));
      if (categoryTriggerValue) categoryTriggerValue.textContent = item.textContent;
      closeCategory();
    });
  });

  // -- Add to calendar: per-card menu (Google / Outlook / .ics) --
  // Times on the page are Riyadh local time (UTC+3, no DST).
  const RIYADH_OFFSET_H = 3;
  const VENUE = 'King Abdulaziz International Conference Center, Riyadh, Saudi Arabia';
  const pad = (n) => String(n).padStart(2, '0');

  const toUtcStamp = (y, mo, d, h, mi) => {
    const dt = new Date(Date.UTC(y, mo - 1, d, h - RIYADH_OFFSET_H, mi));
    return dt.getUTCFullYear() + pad(dt.getUTCMonth() + 1) + pad(dt.getUTCDate())
      + 'T' + pad(dt.getUTCHours()) + pad(dt.getUTCMinutes()) + '00Z';
  };

  const readEvent = (card) => {
    const activeTab = page.querySelector('.fmf-ca-tab.is-active');
    const day = activeTab ? parseInt(activeTab.dataset.day, 10) : 12;
    const timeText = (card.querySelector('.fmf-ca-time-text') || {}).textContent || '';
    const m = timeText.match(/(\d{1,2}):(\d{2})(?:\s*to\s*(\d{1,2}):(\d{2}))?/i);
    if (!m) return null;
    const sh = parseInt(m[1], 10);
    const sm = parseInt(m[2], 10);
    // No end time (e.g. "Closing remarks") -> default to a 1 hour slot
    const eh = m[3] ? parseInt(m[3], 10) : sh + 1;
    const em = m[3] ? parseInt(m[4], 10) : sm;
    const stage = card.querySelector('.fmf-ca-tag-location');
    const title = (card.querySelector('.fmf-ca-card-title') || {}).textContent || 'FMF 2027 session';
    return {
      title: title.replace(/\s+/g, ' ').trim(),
      start: toUtcStamp(2027, 1, day, sh, sm),
      end: toUtcStamp(2027, 1, day, eh, em),
      location: (stage ? stage.textContent.trim() + ', ' : '') + VENUE,
      details: 'Future Minerals Forum 2027 - https://fmf.sa'
    };
  };

  const googleUrl = (ev) => 'https://calendar.google.com/calendar/render?action=TEMPLATE'
    + '&text=' + encodeURIComponent(ev.title)
    + '&dates=' + ev.start + '/' + ev.end
    + '&details=' + encodeURIComponent(ev.details)
    + '&location=' + encodeURIComponent(ev.location);

  const outlookUrl = (ev) => {
    const iso = (s) => s.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6Z');
    return 'https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent'
      + '&subject=' + encodeURIComponent(ev.title)
      + '&startdt=' + encodeURIComponent(iso(ev.start))
      + '&enddt=' + encodeURIComponent(iso(ev.end))
      + '&body=' + encodeURIComponent(ev.details)
      + '&location=' + encodeURIComponent(ev.location);
  };

  const downloadIcs = (ev) => {
    const esc = (s) => s.replace(/([\\;,])/g, '\\$1').replace(/\n/g, '\\n');
    const now = new Date();
    const stamp = now.getUTCFullYear() + pad(now.getUTCMonth() + 1) + pad(now.getUTCDate())
      + 'T' + pad(now.getUTCHours()) + pad(now.getUTCMinutes()) + pad(now.getUTCSeconds()) + 'Z';
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Future Minerals Forum//FMF 2027//EN',
      'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
      'UID:' + ev.start + '-' + Math.abs(ev.title.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)) + '@fmf.sa',
      'DTSTAMP:' + stamp, 'DTSTART:' + ev.start, 'DTEND:' + ev.end,
      'SUMMARY:' + esc(ev.title), 'DESCRIPTION:' + esc(ev.details), 'LOCATION:' + esc(ev.location),
      'END:VEVENT', 'END:VCALENDAR'
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ev.title.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').toLowerCase() + '.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  let calMenu = null;
  let calBtn = null;

  const closeCalMenu = () => {
    if (calMenu) calMenu.remove();
    if (calBtn) calBtn.setAttribute('aria-expanded', 'false');
    calMenu = null;
    calBtn = null;
  };

  const openCalMenu = (btn) => {
    const card = btn.closest('.fmf-ca-card');
    const ev = card && readEvent(card);
    if (!ev) return;
    closeCalMenu();

    const menu = document.createElement('div');
    menu.className = 'fmf-ca-cal-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'Add to calendar');
    menu.innerHTML =
      '<a class="fmf-ca-cal-menu-item" role="menuitem" target="_blank" rel="noopener noreferrer">'
      + '<i class="fa-brands fa-google"></i>Google Calendar</a>'
      + '<a class="fmf-ca-cal-menu-item" role="menuitem" target="_blank" rel="noopener noreferrer">'
      + '<i class="fa-brands fa-microsoft"></i>Outlook</a>'
      + '<button type="button" class="fmf-ca-cal-menu-item" role="menuitem">'
      + '<i class="fa-solid fa-download"></i>Apple / Other (.ics)</button>';
    const [gLink, oLink, icsBtn] = menu.children;
    gLink.href = googleUrl(ev);
    oLink.href = outlookUrl(ev);
    icsBtn.addEventListener('click', () => { downloadIcs(ev); closeCalMenu(); });
    [gLink, oLink].forEach((a) => a.addEventListener('click', () => setTimeout(closeCalMenu, 0)));

    const top = btn.closest('.fmf-ca-card-top');
    top.appendChild(menu);
    menu.style.top = (btn.offsetTop + btn.offsetHeight + 8) + 'px';
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-haspopup', 'menu');
    calMenu = menu;
    calBtn = btn;
    gLink.focus();
  };

  page.querySelectorAll('.fmf-ca-cal-btn').forEach((btn) => {
    btn.setAttribute('aria-haspopup', 'menu');
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (calBtn === btn) closeCalMenu();
      else openCalMenu(btn);
    });
  });

  document.addEventListener('click', (e) => {
    if (calMenu && !calMenu.contains(e.target)) closeCalMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && calMenu) {
      const b = calBtn;
      closeCalMenu();
      if (b) b.focus();
    }
  });

  // Mobile-only: the filter body and category list are floating overlays
  // there (desktop keeps them inline, so this never needs to run there)
  document.addEventListener('click', (e) => {
    if (!window.matchMedia('(max-width: 767.98px)').matches) return;
    if (filterBtn && filterBody && filterBtn.getAttribute('aria-expanded') === 'true'
        && !filterBtn.contains(e.target) && !filterBody.contains(e.target)) {
      closeFilter();
    }
    if (category && category.classList.contains('is-open') && !category.contains(e.target)) {
      closeCategory();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initConferenceAgenda);
} else {
  initConferenceAgenda();
}
